
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Elements } from "@stripe/react-stripe-js";
import { PaymentForm } from "./PaymentForm";
import { CancellationSection } from "./subscription/CancellationSection";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Clock } from "lucide-react";
import { format } from "date-fns";

interface SubscriptionCardProps {
  currentPlan: string | null;
  processingPayment: boolean;
  showPaymentDialog: boolean;
  selectedPlan: string | null;
  stripePromise: Promise<any> | null;
  onSubscribe: (planType: string) => void;
  onCancel: () => void;
  cancelling: boolean;
  onPaymentSuccess: (paymentMethodId: string) => void;
  onPaymentError: (error: Error) => void;
  setShowPaymentDialog: (value: boolean) => void;
  trialEndsAt?: string | null;
  accessEndsAt?: string | null;
}

export const SubscriptionCard = ({
  currentPlan,
  processingPayment,
  showPaymentDialog,
  selectedPlan,
  stripePromise,
  onSubscribe,
  onCancel,
  cancelling,
  onPaymentSuccess,
  onPaymentError,
  setShowPaymentDialog,
  trialEndsAt,
  accessEndsAt
}: SubscriptionCardProps) => {
  const { t } = useTranslation();
  const [activePlan, setActivePlan] = useState<string>(currentPlan || 'monthly');

  // Define price IDs
  const priceIds = {
    monthly: "price_1QvOaiG3RRUf49cQgmkP8MtT",
    yearly: "price_1QvObYG3RRUf49cQp6XPRkNN",
    lifetime: "price_1QvOcGG3RRUf49cQlU0O22em"
  };

  // Map price IDs to plan types
  const priceIdToPlanMap = {
    "price_1QvOaiG3RRUf49cQgmkP8MtT": "monthly",
    "price_1QvObYG3RRUf49cQp6XPRkNN": "yearly",
    "price_1QvOcGG3RRUf49cQlU0O22em": "lifetime"
  };

  // Format date helper function
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  const trialEndDate = formatDate(trialEndsAt);
  const accessEndDate = formatDate(accessEndsAt);
  const isTrialing = currentPlan === 'trialing';
  const isCanceled = currentPlan === 'canceled';

  // Determine the appropriate message based on the selected plan
  const getUpgradeMessage = () => {
    const planType = selectedPlan ? (priceIdToPlanMap[selectedPlan] || selectedPlan) : null;
    if (planType === 'lifetime') {
      return "Your current subscription will be cancelled and you will be charged a one-time payment.";
    } else {
      return "Your current subscription will be automatically updated with prorated billing.";
    }
  };

  console.log("Current plan in SubscriptionCard:", currentPlan);
  console.log("Access ends at:", accessEndsAt);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("common.subscription")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isTrialing && trialEndDate && (
          <div className="bg-blue-50 p-4 rounded mb-6 text-center border border-blue-200">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-5 w-5 text-blue-500 mr-2" />
              <p className="font-medium text-blue-700">
                Trial Active
              </p>
            </div>
            <p className="text-blue-600">
              Your trial will expire on {trialEndDate}. Subscribe to a plan below to continue using premium features.
            </p>
          </div>
        )}

        {isCanceled && accessEndDate && (
          <div className="bg-amber-50 p-4 rounded mb-6 text-center border border-amber-200">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-5 w-5 text-amber-500 mr-2" />
              <p className="font-medium text-amber-700">
                Subscription Canceled
              </p>
            </div>
            <p className="text-amber-600">
              Your subscription has been canceled. You will continue to have access until {accessEndDate}.
            </p>
          </div>
        )}

        {!currentPlan && !isTrialing && !isCanceled && (
          <div className="bg-yellow-100 p-4 rounded mb-6 text-center">
            <p className="font-medium text-yellow-800">
              You don't have an active subscription.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <Card className="relative transform transition-all duration-200 hover:scale-105">
            <CardHeader className="pb-8 text-center">
              <CardTitle className="text-xl">Monthly</CardTitle>
              <p className="text-3xl font-bold mt-4">$4.99<span className="text-lg text-gray-500">/mo</span></p>
              <p className="text-sm text-gray-500 mt-2">Billed monthly at $4.99</p>
            </CardHeader>
            <CardContent>
              <button 
                className={`w-full px-4 py-2 rounded text-white transition-colors ${
                  currentPlan === 'monthly'
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                onClick={() => onSubscribe(priceIds.monthly)}
                disabled={currentPlan === 'monthly' || processingPayment}
              >
                {currentPlan === 'monthly' ? 'Current Plan' : 'Get Started'}
              </button>
            </CardContent>
          </Card>

          <Card className="relative border-primary transform transition-all duration-200 hover:scale-105">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-max">
              <span className="bg-primary text-white px-4 py-1 rounded-full text-sm whitespace-nowrap">
                Most Popular
              </span>
            </div>
            <CardHeader className="pb-8 text-center">
              <CardTitle className="text-xl">Yearly</CardTitle>
              <p className="text-3xl font-bold mt-4">12.50<span className="text-lg text-gray-500">/mo</span></p>
              <p className="text-sm text-gray-500 mt-2">Billed annually at $29.99</p>
            </CardHeader>
            <CardContent>
              <button 
                className={`w-full px-4 py-2 rounded text-white transition-colors ${
                  currentPlan === 'yearly' 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                onClick={() => onSubscribe(priceIds.yearly)}
                disabled={currentPlan === 'yearly' || processingPayment}
              >
                {currentPlan === 'yearly' ? 'Current Plan' : 'Get Started'}
              </button>
            </CardContent>
          </Card>

          <Card className="relative transform transition-all duration-200 hover:scale-105">
            <CardHeader className="pb-8 text-center">
              <CardTitle className="text-xl">Lifetime</CardTitle>
              <p className="text-3xl font-bold mt-4">$199.99</p>
              <p className="text-sm text-gray-500 mt-2">One-time payment</p>
            </CardHeader>
            <CardContent>
              <button 
                className={`w-full px-4 py-2 rounded text-white transition-colors ${
                  currentPlan === 'lifetime' 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                onClick={() => onSubscribe(priceIds.lifetime)}
                disabled={currentPlan === 'lifetime' || processingPayment}
              >
                {currentPlan === 'lifetime' ? 'Current Plan' : 'Get Started'}
              </button>
            </CardContent>
          </Card>
        </div>

        {(currentPlan && !isTrialing && !isCanceled) ? (
          <CancellationSection 
            onCancel={onCancel} 
            processingPayment={processingPayment}
            cancelling={cancelling}
          />
        ) : null}

        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Subscribe to {selectedPlan ? (priceIdToPlanMap[selectedPlan] || 'Selected') : ''} Plan
              </DialogTitle>
              <DialogDescription>
                {currentPlan ? 
                  `You are changing from ${currentPlan} to ${selectedPlan ? (priceIdToPlanMap[selectedPlan] || 'selected') : ''} plan.` : 
                  `You are subscribing to the ${selectedPlan ? (priceIdToPlanMap[selectedPlan] || 'selected') : ''} plan.`}
              </DialogDescription>
            </DialogHeader>
            
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4 mr-2" />
              <AlertDescription>
                {currentPlan ? 
                  getUpgradeMessage() : 
                  "You'll be charged immediately for this subscription."}
              </AlertDescription>
            </Alert>
            
            {stripePromise && (
              <Elements stripe={stripePromise}>
                <PaymentForm
                  onSuccess={onPaymentSuccess}
                  onError={onPaymentError}
                  planType={selectedPlan || ''}
                  loading={processingPayment}
                />
              </Elements>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
